import {
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  makeStyles,
  Theme
} from '@material-ui/core';
import {
  Close,
  Settings,
  List as ListIcon
} from '@material-ui/icons';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { AditamentoStatus, InstrumentoFinanceiro } from '../../common/genericTypes';
import { MdSpace } from 'm2m.components';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { addAlert } from 'store/modules/snackBar';
import { detectFieldType, getArrayType, getFieldIcon, ListagemDiferencas } from './listagemDiferencas';
import { convertDivergenciasToAditamento, isDivergenciasData } from './divergenciasAdapter';
import { DivergenciasData, reprocessarValidacao } from '../../requests/divergencias';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogTitle: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: theme.palette.grey[50],
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(2, 3)
    },
    dialogContent: {
      padding: theme.spacing(3),
      backgroundColor: theme.palette.background.default
    },
    headerInfo: {
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${theme.palette.divider}`,
      marginBottom: theme.spacing(2)
    },
    tableContainer: {
      maxHeight: '60vh',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius
    },
    tableHeader: {
      backgroundColor: theme.palette.grey[100],
      '& .MuiTableCell-head': {
        fontWeight: 600,
        color: theme.palette.text.primary
      }
    },
    fieldName: {
      fontWeight: 500,
      color: theme.palette.primary.main
    },
    oldValue: {
      color: theme.palette.error.main,
      fontFamily: 'monospace',
      backgroundColor: theme.palette.error.light + '20',
      padding: theme.spacing(0.5, 1),
      borderRadius: theme.shape.borderRadius,
      display: 'inline-block'
    },
    newValue: {
      color: theme.palette.success.main,
      fontFamily: 'monospace',
      backgroundColor: theme.palette.success.light + '20',
      padding: theme.spacing(0.5, 1),
      borderRadius: theme.shape.borderRadius,
      display: 'inline-block'
    },
    editableField: {
      '& .MuiOutlinedInput-root': {
        backgroundColor: theme.palette.background.paper
      }
    },
    actionButtons: {
      display: 'flex',
      gap: theme.spacing(1)
    },
    statusChip: {
      padding: theme.spacing(0.5, 1.5),
      borderRadius: theme.spacing(1),
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase'
    },
    statusEmAnalise: {
      backgroundColor: theme.palette.warning.light,
      color: theme.palette.warning.contrastText
    },
    statusAprovado: {
      backgroundColor: theme.palette.success.light,
      color: theme.palette.success.contrastText
    },
    statusReprovado: {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.contrastText
    },
    groupAccordion: {
      marginBottom: theme.spacing(1),
      '&:before': {
        display: 'none'
      },
      '& .MuiAccordionSummary-root': {
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.shape.borderRadius,
        minHeight: 48,
        '&.Mui-expanded': {
          minHeight: 48
        }
      },
      '& .MuiAccordionDetails-root': {
        padding: theme.spacing(1, 2, 2, 2)
      }
    },
    groupTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      fontWeight: 500
    },
    fieldTypeIcon: {
      fontSize: '1.2rem',
      color: theme.palette.primary.main
    },
    complexValue: {
      backgroundColor: theme.palette.grey[50],
      padding: theme.spacing(1),
      borderRadius: theme.shape.borderRadius,
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      border: `1px solid ${theme.palette.divider}`,
      maxWidth: '100%',
      overflow: 'auto'
    },
    arrayBadge: {
      backgroundColor: theme.palette.info.light,
      color: theme.palette.info.contrastText,
      fontSize: '0.7rem',
      padding: theme.spacing(0.25, 0.75),
      borderRadius: theme.spacing(1),
      marginLeft: theme.spacing(1)
    },
    pathBreadcrumb: {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      fontFamily: 'monospace',
      backgroundColor: theme.palette.grey[100],
      padding: theme.spacing(0.25, 0.5),
      borderRadius: theme.shape.borderRadius,
      marginTop: theme.spacing(0.5)
    },
    nestedField: {
      paddingLeft: theme.spacing(2),
      borderLeft: `2px solid ${theme.palette.divider}`,
      marginLeft: theme.spacing(1)
    },
    countChange: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      '& .count-badge': {
        backgroundColor: theme.palette.secondary.light,
        color: theme.palette.secondary.contrastText,
        padding: theme.spacing(0.25, 0.75),
        borderRadius: theme.spacing(1),
        fontSize: '0.75rem',
        fontWeight: 600
      }
    }
  })
);

export interface AditamentoDiferenca {
  tipoClasse: string;
  nomeCampo: string;
  newField: string;
  oldField: string;
  caminhoCompleto: string;
  enumValues?: string[];
}

export interface AditamentoRevisionData {
  aditamento: {
    id: number;
    instrumentoFinanceiroGuid: string;
    statusAditamentoId: number;
    tipoInstrumentoFinanceiro: string;
    usuarioId: string;
    dataDeCriacao: string;
    dataUltimaAlteracao: string;
    aditamentoCount: number;
    aditamentoGuid: string;
    new: string;
    old: string;
  };
  diferencas: AditamentoDiferenca[];
  instrumentoCodigo: string;
}

export interface AditamentoRevisionModalProps {
  open: boolean;
  onClose: (result?: { success: boolean; message: string }) => void;
  data: AditamentoRevisionData | null;
  onSave?: (updatedDiferencas: AditamentoDiferenca[]) => void;
  allowEdit?: boolean;
  
  divergenciasData?: DivergenciasData | null;
  mode?: 'aditamento' | 'divergencias';
}

const statusLabels: { [key: number]: string } = {
  [AditamentoStatus.EmAnalise]: 'Em Análise',
  [AditamentoStatus.Aprovado]: 'Aprovado',
  [AditamentoStatus.Reprovado]: 'Reprovado',
};

export interface FieldGroupAditamento {
  groupName: string;
  groupType: 'array' | 'object' | 'simple';
  icon: React.ReactNode;
  fields: AditamentoDiferenca[];
  isExpanded?: boolean;
  arrayType?: 'amortizacoes' | 'pagamentojuros' | 'incorporacaojuros' | 'generic';
}

const groupRelatedFields = (diferencas: AditamentoDiferenca[]): FieldGroupAditamento[] => {
  const groups: { [key: string]: FieldGroupAditamento } = {};
  const ungroupedFields: AditamentoDiferenca[] = [];

  diferencas.forEach(diferenca => {
    const path = diferenca.caminhoCompleto;
    const fieldName = diferenca.nomeCampo.toLowerCase();

    let basePath = path;
    let groupType: 'array' | 'object' | 'simple' = 'simple';
    let arrayType: 'amortizacoes' | 'pagamentojuros' | 'incorporacaojuros' | 'generic' = 'generic';

    const isJsonArray = (diferenca.newField && diferenca.newField.startsWith('[')) ||
      (diferenca.oldField && diferenca.oldField.startsWith('['));

    if (isJsonArray) {
      groupType = 'array';
      basePath = diferenca.caminhoCompleto;
      arrayType = getArrayType(fieldName);
    } else if (path.includes('[') && path.includes(']')) {
      basePath = path.substring(0, path.indexOf('['));
      groupType = 'array';
      arrayType = getArrayType(basePath);
    } else if (path.includes('.') && !path.includes('.Count')) {
      const parts = path.split('.');
      if (parts.length > 1) {
        basePath = parts[0];
        groupType = 'object';
      }
    }

    if (groupType !== 'simple' && (basePath !== path || isJsonArray)) {
      if (!groups[basePath]) {
        groups[basePath] = {
          groupName: basePath,
          groupType,
          icon: groupType === 'array' ? <ListIcon /> : <Settings />,
          fields: [],
          isExpanded: arrayType !== 'generic',
          arrayType
        };
      }
      groups[basePath].fields.push(diferenca);
    } else {
      ungroupedFields.push(diferenca);
    }
  });

  const result: FieldGroupAditamento[] = Object.values(groups);

  ungroupedFields.forEach(diferenca => {
    const fieldType = detectFieldType(diferenca);
    result.push({
      groupName: diferenca.nomeCampo,
      groupType: 'simple',
      icon: getFieldIcon(fieldType),
      fields: [diferenca],
      isExpanded: true
    });
  });

  return result.sort((a, b) => {
    if (a.groupType !== b.groupType) {
      const order = { simple: 0, object: 1, array: 2 };
      return order[a.groupType] - order[b.groupType];
    }
    return a.groupName.localeCompare(b.groupName);
  });
};

const getStatusClassName = (statusId: number, classes: any): string => {
  switch (statusId) {
    case AditamentoStatus.EmAnalise:
      return classes.statusPendente;
    case AditamentoStatus.Aprovado:
      return classes.statusAprovado;
    case AditamentoStatus.Reprovado:
      return classes.statusReprovado;
    default:
      return classes.statusPendente;
  }
};

export const AditamentoRevisionModal: React.FC<AditamentoRevisionModalProps> = ({
  open,
  onClose,
  data,
  onSave,
  allowEdit = false,
  divergenciasData,
  mode = 'aditamento',
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editedDiferencas, setEditedDiferencas] = useState<AditamentoDiferenca[]>([]);
  const [reprocessandoValidacao, setReprocessandoValidacao] = useState<boolean>(false);
  const putAditamentoCertificadoRecebiveis = useSelector((state: RootState) => state.certificadoRecebiveisApi.putAditamentoCertificadoRecebiveis);
  const putAditamentoCra = useSelector((state: RootState) => state.craApi.putAditamentoCra);
  const putAditamentoCdca = useSelector((state: RootState) => state.cdcaApi.putAditamentoCdca);
  const putAditamentoCotasDeFundo = useSelector((state: RootState) => state.cotasDeFundoApi.putAditamentoCotasDeFundo);
  const putAditamentoCri = useSelector((state: RootState) => state.criApi.putAditamentoCri);
  const putAditamentoDebenture = useSelector((state: RootState) => state.debentureApi.putAditamentoDebenture);
  const putAditamentoNotaComercial = useSelector((state: RootState) => state.notaComercialApi.putAditamentoNotaComercial);

  const processedData = useMemo(() => {
    if (mode === 'divergencias' && divergenciasData) {
      return convertDivergenciasToAditamento(divergenciasData);
    }
    return data;
  }, [mode, divergenciasData, data]);

  const isDivergenciasMode = mode === 'divergencias';
  const canEdit = allowEdit && !isDivergenciasMode;
  const isStatusErro = isDivergenciasMode && divergenciasData?.validacao?.status === 'ERRO';

  React.useEffect(() => {
    if (processedData?.diferencas) {
      setEditedDiferencas([...processedData.diferencas]);
    }
  }, [processedData]);

  const handleEditField = useCallback((caminhoCompleto: string) => {
    setEditingField(caminhoCompleto);
  }, []);

  const handleSaveField = useCallback(() => {
    setEditingField(null);
  }, []);

  const handleCancelEdit = useCallback(() => {
    if (processedData?.diferencas) {
      setEditedDiferencas([...processedData.diferencas]);
    }
    setEditingField(null);
  }, [processedData]);

  const handleDifferencesUpdate = useCallback((updatedDifferences: AditamentoDiferenca[]) => {
    setEditedDiferencas(updatedDifferences);
  }, []);

  const handleDialogClose = useCallback((event: {}, reason: "backdropClick" | "escapeKeyDown") => {
    onClose();
  }, [onClose]);

  const handleFieldValueChange = useCallback((caminhoCompleto: string, newValue: string) => {
    if (newValue === 'array-update-start' || newValue === 'array-update-end') {
      return;
    }

    if (newValue === 'DELETE_FIELD') {
      setEditedDiferencas(prev => prev.filter(diff => diff.caminhoCompleto.toLowerCase() !== caminhoCompleto.toLowerCase()));
      return;
    }

    setEditedDiferencas(prev => {
      const existingDiffIndex = prev.findIndex(diff => diff.caminhoCompleto.toLowerCase() === caminhoCompleto.toLowerCase());

      if (existingDiffIndex !== -1) {
        return prev.map((diff, index) =>
          index === existingDiffIndex
            ? { ...diff, newField: newValue }
            : diff
        );
      } else {
        const arrayMatch = caminhoCompleto.toLowerCase().match(/(.+?)\[(\d+)\]\.(.+)/);

        if (arrayMatch) {
          const [, arrayPath, fieldName] = arrayMatch;

          const similarField = prev.find(diff => {
            const match = diff.caminhoCompleto.toLowerCase().match(/(.+?)\[(\d+)\]\.(.+)/);
            return match && match[1] === arrayPath && match[3] === fieldName;
          });

          const newDiff = {
            tipoClasse: similarField?.tipoClasse || 'System.Object',
            nomeCampo: fieldName,
            newField: newValue,
            oldField: 'null',
            caminhoCompleto: caminhoCompleto
          };

          return [...prev, newDiff];
        }

        return prev;
      }
    });
  }, []);

  const handleReprocessarValidacao = useCallback(async () => {
    if (!isDivergenciasMode || !divergenciasData?.validacao?.instrumentoId) return;

    try {
      setReprocessandoValidacao(true);
      
      const instrumentoId = divergenciasData.validacao.instrumentoId.toString();
      
      await reprocessarValidacao(
        dispatch,
        { instrumentoFinanceiroId: instrumentoId },
        true
      );
      
      dispatch(addAlert({ 
        severity: 'success', 
        message: 'Instrumento enviado para reprocessamento com sucesso. Atualize a página ou retorne aqui após alguns minutos para conferir o status.' 
      }));
      
      onClose({ success: true, message: 'Reprocessamento iniciado com sucesso!' });
      
    } catch (error) {
      console.error('Erro ao reprocessar validação:', error);
      dispatch(addAlert({ 
        severity: 'error', 
        message: 'Erro ao enviar para reprocessamento. Tente novamente.' 
      }));
    } finally {
      setReprocessandoValidacao(false);
    }
  }, [isDivergenciasMode, divergenciasData, dispatch, onClose]);

  const handleSaveAll = useCallback(async () => {
    if (onSave) {
      onSave(editedDiferencas);
    }

    try {
      switch (processedData?.aditamento.tipoInstrumentoFinanceiro) {
        case InstrumentoFinanceiro.CertificadoRecebiveis:
          await putAditamentoCertificadoRecebiveis(dispatch, processedData.aditamento.id.toString(), editedDiferencas);
          break;
        case InstrumentoFinanceiro.Cra:
          await putAditamentoCra(dispatch, processedData.aditamento.id.toString(), editedDiferencas);
          break;
        case InstrumentoFinanceiro.Cdca:
          await putAditamentoCdca(dispatch, processedData.aditamento.id.toString(), editedDiferencas);
          break;
        case InstrumentoFinanceiro.CotasDeFundo:
          await putAditamentoCotasDeFundo(dispatch, processedData.aditamento.id.toString(), editedDiferencas);
          break;
        case InstrumentoFinanceiro.Cri:
          await putAditamentoCri(dispatch, processedData.aditamento.id.toString(), editedDiferencas);
          break;
        case InstrumentoFinanceiro.Debenture:
          await putAditamentoDebenture(dispatch, processedData.aditamento.id.toString(), editedDiferencas);
          break;
        case InstrumentoFinanceiro.NotaComercial:
          await putAditamentoNotaComercial(dispatch, processedData.aditamento.id.toString(), editedDiferencas);
          break;
        default:
          console.warn('Tipo de instrumento financeiro não reconhecido:', processedData?.aditamento.tipoInstrumentoFinanceiro);
          onClose({ success: false, message: 'Tipo de instrumento financeiro não reconhecido' });
          return;
      }
      
      onClose({ success: true, message: 'Alterações salvas com sucesso!' });
    } catch (error) {
      console.error('Erro ao salvar alterações do aditamento:', error);
      onClose({ success: false, message: 'Erro ao salvar alterações. Tente novamente.' });
    }
  }, [editedDiferencas, onSave, onClose, dispatch, processedData]);

  if (!processedData) {
    return null;
  }

  const { aditamento, diferencas, instrumentoCodigo } = processedData;
  const isPendente = aditamento.statusAditamentoId === AditamentoStatus.EmAnalise;
  const fieldGroups = groupRelatedFields(editedDiferencas);

  const modalTitle = isDivergenciasMode 
    ? (isStatusErro ? `⚠️ Erro na Validação - ${instrumentoCodigo}` : `🚨 Divergências Identificadas - ${instrumentoCodigo}`)
    : `Revisão de Alterações - ${instrumentoCodigo} - Aditamento #${aditamento.aditamentoCount}`;

  const sectionTitle = isDivergenciasMode 
    ? (isStatusErro ? 'Erro na Validação' : `Divergências Encontradas (${diferencas.length})`)
    : 'Alterações Propostas';

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: { minHeight: '70vh' }
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6" component="div">
          {modalTitle}
        </Typography>
        <IconButton onClick={() => onClose()} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <div className={classes.headerInfo}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Instrumento Financeiro
              </Typography>
              <Typography variant="body1" gutterBottom>
                {instrumentoCodigo}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Tipo
              </Typography>
              <Typography variant="body1" gutterBottom>
                {aditamento.tipoInstrumentoFinanceiro.replaceAll('_', ' ')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Status
              </Typography>
              <span
                className={`${classes.statusChip} ${getStatusClassName(aditamento.statusAditamentoId, classes)}`}
              >
                {isDivergenciasMode && divergenciasData?.validacao?.status 
                  ? divergenciasData.validacao.status 
                  : (statusLabels[aditamento.statusAditamentoId] || 'Desconhecido')
                }
              </span>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Data de Criação
              </Typography>
              <Typography variant="body1" gutterBottom>
                {moment(aditamento.dataDeCriacao).format('DD/MM/YYYY')}
              </Typography>
            </Grid>
          </Grid>
        </div>

        <MdSpace />

        <Typography variant="h6" gutterBottom>
          {sectionTitle}
        </Typography>

        {isDivergenciasMode && !isStatusErro && (
          <div style={{ backgroundColor: '#fff3cd', padding: 16, borderRadius: 8, marginBottom: 16 }}>
            <Typography variant="body2" style={{ color: '#856404' }}>
              <strong>ℹ️ Sobre as Divergências:</strong><br />
              Os campos abaixo apresentam diferenças entre os dados salvos no sistema IMF e os dados 
              provenientes do arquivo do termo constitutivo importado via FRONTIER.<br /><br />
              <strong>🟡 Amarelo:</strong> Valores do sistema IMF (precisam ser corrigidos)<br />
              <strong>🟢 Verde:</strong> Valores da minuta/termo constitutivo (valores corretos)
            </Typography>
          </div>
        )}

        {isStatusErro && (
          <div style={{ backgroundColor: '#f8d7da', padding: 16, borderRadius: 8, marginBottom: 16 }}>
            <Typography variant="body2" style={{ color: '#721c24' }}>
              <strong>⚠️ Erro na Validação:</strong><br />
              O instrumento apresentou erro durante o processo de validação automática. 
              Não foi possível processar as informações do {divergenciasData?.instrumentoTipo === 'DEBENTURE' ? 'Escritura de Emissão' : 'Termo Constitutivo'}.<br /><br />
              <strong>🔄 Reprocessamento:</strong> Clique no botão abaixo para enviar o instrumento 
              para reprocessamento manual. O sistema tentará validar novamente as informações.<br /><br />
              <strong>📋 Observação:</strong> Caso a tentativa de reprocessamento manual não seja bem-sucedida, 
              verifique se o documento está correto e 
              possui todas as informações necessárias para a validação.
            </Typography>
          </div>
        )}

        {!isStatusErro && (
          <>
            <TableContainer className={classes.tableContainer}>
              <Table stickyHeader>
                <TableHead className={classes.tableHeader}>
                  <TableRow>
                    <TableCell>Campo</TableCell>
                    <TableCell>
                      {isDivergenciasMode ? 'Valor no Sistema IMF' : 'Valor Anterior'}
                    </TableCell>
                    <TableCell>
                      {isDivergenciasMode ? 'Valor do Termo Constitutivo' : 'Novo Valor'}
                    </TableCell>
                    {canEdit && <TableCell align="center">Ações</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fieldGroups.map((group, groupIndex) => (
                    <ListagemDiferencas
                      key={`group-${groupIndex}`}
                      group={group}
                      canEdit={canEdit}
                      editingField={editingField}
                      classes={classes}
                      onEditField={handleEditField}
                      onSaveField={handleSaveField}
                      onCancelEdit={handleCancelEdit}
                      onFieldValueChange={handleFieldValueChange}
                      onDifferencesUpdate={handleDifferencesUpdate}
                      allDifferences={editedDiferencas}
                      isDivergenciasMode={isDivergenciasMode}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {diferencas.length === 0 && (
              <Typography variant="body2" color="textSecondary" align="center" style={{ padding: 20 }}>
                Nenhuma alteração encontrada.
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions style={{ padding: 16 }}>
        <Button onClick={() => onClose()} color="secondary" disabled={reprocessandoValidacao}>
          Fechar
        </Button>
        {isDivergenciasMode && isStatusErro && (
          <Button 
            onClick={handleReprocessarValidacao} 
            variant="contained"
            disabled={reprocessandoValidacao}
            style={{
              backgroundColor: '#dc3545',
              color: 'white'
            }}
          >
            {reprocessandoValidacao ? '⏳ Enviando...' : '🔄 Reprocessar Validação'}
          </Button>
        )}
        {canEdit && onSave && (
          <Button onClick={handleSaveAll} color="primary" variant="contained" disabled={reprocessandoValidacao}>
            Salvar Alterações
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
